import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import { stopRingtone, rejectConsultation } from "../../store/videoConsult-actions";
import { videoConsultActions } from "../../store/videoConsult-slice";
import { consultOrderActions } from "../../store/consultOrder-slice";
import AudioPlayer from "../../utils/audioUtils";
import styles from "./VideoConsultAlert.module.scss";
import { getTokenUserId } from "../../utils/tokenUtils";

interface VideoConsultAlertProps {
  className?: string;
  consultantId?: number;
}

// The alert renders at most once per app, so a static id is safe.
const SOUND_STATUS_ID = "video-consult-sound-status";

const SOUND_ISSUE_COPY: Record<"blocked" | "failed", string> = {
  blocked:
    "Browser je blokirao automatsko puštanje zvuka za dolaznu konsultaciju.",
  failed: "Zvučno obaveštenje nije moglo da se pusti.",
};

const VideoConsultAlert: React.FC<VideoConsultAlertProps> = ({
                                                               className = "",
                                                               consultantId: consultantIdProp,
                                                             }) => {
  const dispatch = useDispatch();
  const { incomingConsults, playRingtone } = useSelector(
    (state: RootState) => state.videoConsult
  );
  const token = useSelector((state: RootState) => state.auth.token);
  // ITAM-2887: logout is pausing availability; joining now would be undone
  // by the `auth/logout` that follows in under 3 s.
  const loggingOut = useSelector(
    (state: RootState) => state.auth.loggingOut === true,
  );

  const [busy, setBusy] = useState(false);
  const audioPlayer = AudioPlayer.getInstance();

  // Identity of the call currently on screen. The ringing effect keys on this
  // ALONGSIDE the list length, and needs both: one poll can retire call A and
  // pick up call B with the length never leaving 1 (a length-keyed effect
  // would leave B ringing under A's notification, dismissed already in the
  // worst case), while a call queueing up behind an unchanged head moves only
  // the length (and is worth another play attempt plus a fresh notification).
  //
  // INVARIANT this relies on: a consultant is offered ZERO OR ONE waiting call
  // at a time (the backend hands a pharmacist the call it routed to them, and
  // this component only ever renders the head). Head-plus-length therefore
  // identifies the ringing call exactly. If the backend ever starts returning
  // several simultaneous waiting calls, a same-shape queue rotation such as
  // [A, B] -> [A, C] would move neither key, and this needs per-id tracking.
  const ringingConsultId = incomingConsults[0]?.id ?? null;
  const ringingConsultCount = incomingConsults.length;

  // Resolve consultantId: prop > token
  const consultantId =
      typeof consultantIdProp === "number"
          ? consultantIdProp
          : token
              ? getTokenUserId(token)
              : undefined;

  // Why the incoming call is ringing without sound ("blocked" = autoplay
  // policy, "failed" = any other playback error), or null when sound is
  // fine. Non-null renders the explicit "Pokreni zvuk" control with
  // cause-specific copy.
  const [soundIssue, setSoundIssue] = useState<"blocked" | "failed" | null>(
    null
  );

  // Announce the incoming call on every channel this page has.
  //
  // 1. The desktop notification fires for EVERY ringing call, independently of
  //    whether the sound plays: it is the only channel that reaches a
  //    pharmacist who is in another tab, behind another window, or on a tab
  //    whose sound is muted, and a duplicated signal costs a notification the
  //    user dismisses while a missing one costs the call. It stays tied to the
  //    ringing state, so the cleanup below takes it down the moment the call is
  //    answered, rejected or gone. Delivery only: this path never ASKS for the
  //    permission, it checks it. A request needs a real user gesture to produce
  //    a real prompt (Firefox and Safari require one; Chrome demotes a
  //    gesture-less request to a quiet address-bar icon), and an incoming call
  //    is not a gesture: VideoConsultReadiness owns that, from a click.
  //
  // 2. The ringtone. Autoplay without user activation can be rejected
  //    (NotAllowedError, the browser autoplay policy), and this effect is the
  //    only automatic play trigger for the whole incoming call, so a blocked
  //    attempt must not end the story: surface an explicit retry control (a
  //    visible button beats capturing an unrelated click: synthetic clicks
  //    carry no activation and the user should see what is happening).
  useEffect(() => {
    if (!(playRingtone && ringingConsultId !== null)) {
      setSoundIssue(null);
      audioPlayer.stopRingtone();
      return;
    }

    let disposed = false;
    let notification: Notification | null = null;

    if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      try {
        notification = new Notification("Dolazna video konsultacija", {
          icon: "/notification-icon.png",
          requireInteraction: true,
        });
        notification.onclick = () => window.focus();
      } catch {
        // Constructor can throw (e.g. insecure context); the on-screen
        // alert is still rendered either way.
      }
    }

    audioPlayer.playRingtone().then((outcome) => {
      if (disposed) return;

      // "superseded": a newer attempt (or a stop) owns the sound state now.
      if (outcome === "superseded") return;

      // A later automatic attempt can succeed while a sound issue is still
      // showing (e.g. a second consult re-runs this effect after the user
      // interacted elsewhere), so the stale retry control must clear.
      // "failed" (non-policy error) gets the same recovery surface as
      // "blocked": the user may still rescue the call this way.
      const playing = outcome === "started" || outcome === "already-playing";
      setSoundIssue(playing ? null : outcome);
    });

    return () => {
      disposed = true;
      notification?.close();
    };
  }, [playRingtone, ringingConsultId, ringingConsultCount, audioPlayer]);

  // Explicit user-gesture retry: play() runs synchronously inside this click
  // (see AudioPlayer). If the browser still refuses, the control stays
  // visible for another try, with copy matching the latest cause.
  const handleRetrySound = () => {
    audioPlayer.playRingtone().then((outcome) => {
      if (outcome === "started" || outcome === "already-playing") {
        setSoundIssue(null);
      } else if (outcome === "blocked" || outcome === "failed") {
        setSoundIssue(outcome);
      }
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      audioPlayer.stopRingtone();
      if (audioPlayer.isRingtonePlaying()) {
        dispatch(stopRingtone() as any);
      }
    };
  }, [audioPlayer, dispatch]);

  // Don't render if no consultations
  if (incomingConsults.length === 0) {
    return null;
  }

  // Get the first (most recent) consultation
  const consultation = incomingConsults[0];

  const handleJoinConsultation = () => {
    if (loggingOut) {
      return;
    }
    // Stop ringtone immediately when user responds
    audioPlayer.stopRingtone();
    dispatch(stopRingtone() as any);

    // PiP: mount the floating video panel instead of opening a Daily tab.
    dispatch(videoConsultActions.setActiveConsult(consultation));

    // ITAM-2583: arm the consult-order lifecycle (the Join signal only). The
    // click-arm stays: it is the one arm signal that cannot be missed
    // (synchronous dispatch in the click handler); VideoConsultManager adds a
    // second arm on Daily's joined-meeting event (plan 6.1). The app-level
    // ConsultOrderManager takes over via the prefill endpoint.
    dispatch(consultOrderActions.armConsultOrder({ consultId: consultation.id }));
  };

  const handleDismiss = async () => {
    // Stop ringtone when dismissed
    audioPlayer.stopRingtone();
    dispatch(stopRingtone() as any);

    if (!consultantId) {
      console.warn("[VideoConsultAlert] Missing consultantId for reject");
      return;
    }
    if (!consultation.roomName) {
      console.warn("[VideoConsultAlert] Missing roomName for reject");
      return;
    }

    try {
      setBusy(true);
      await (dispatch as any)(
          rejectConsultation(consultation.roomName, consultantId)
      );
    } catch (e) {
      console.error(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${styles.videoConsultAlert} ${className}`}>
      {/* Always-mounted live region: mounting an already-populated status
          node is announced unreliably: screen readers pick up text CHANGES
          in an existing region, so this stays in the tree and only its text
          flips with the sound issue. */}
      <span id={SOUND_STATUS_ID} role="status" className={styles.srOnly}>
        {soundIssue ? SOUND_ISSUE_COPY[soundIssue] : ""}
      </span>
      <div className={styles.alertContent}>
        <div className={styles.alertIcon}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3513 21.402C21.1454 21.5905 20.9013 21.7346 20.6355 21.8259C20.3698 21.9172 20.0874 21.9537 19.81 21.9329C16.7428 21.6565 13.787 20.5821 11.19 18.7829C8.77382 17.1292 6.72533 14.9549 5.20998 12.4229C3.32997 9.77127 2.25005 6.75234 1.99998 3.62288C1.97916 3.34728 2.01536 3.06789 2.10619 2.80405C2.19702 2.54022 2.33979 2.29811 2.52611 2.09411C2.71244 1.8901 2.93929 1.72792 3.19219 1.61705C3.44509 1.50618 3.71724 1.44951 3.99248 1.45038H6.99248C7.48125 1.44544 7.95681 1.63381 8.3249 1.97352C8.693 2.31323 8.92171 2.77705 8.99248 3.27288C9.12434 4.26437 9.35902 5.23886 9.69248 6.17288C9.83484 6.52052 9.87473 6.90068 9.80876 7.26835C9.74279 7.63602 9.57378 7.97693 9.31998 8.24288L8.08998 9.47288C9.513 11.9951 11.4924 13.9745 14.0155 15.3979L15.2455 14.1679C15.5115 13.9141 15.8524 13.7451 16.22 13.6791C16.5877 13.6132 16.9679 13.653 17.3155 13.7954C18.2496 14.1288 19.224 14.3635 20.2155 14.4954C20.7177 14.5665 21.1859 14.8009 21.5265 15.1774C21.8671 15.5539 22.0516 16.0378 22.0422 16.5329L22 16.92Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className={styles.alertText}>
          <div className={styles.alertTitle}>Dolazna video konsultacija</div>
          <div className={styles.alertSubtitle}>
            Pacijent: {consultation?.callerInfo?.joinName?.trim?.() || "Priključuje se"}
          </div>
          <div className={styles.alertTime}>
            Email: {consultation.callerInfo.userEmail}
          </div>
          <div className={styles.alertTime}>
            Trajanje: {consultation.meetingDuration}
          </div>
        </div>

        <div className={styles.alertActions}>
          {soundIssue && (
            <button
              className={styles.soundRetryButton}
              onClick={handleRetrySound}
              aria-describedby={SOUND_STATUS_ID}
              title={SOUND_ISSUE_COPY[soundIssue]}
            >
              Pokreni zvuk
            </button>
          )}
          <button
            className={styles.joinButton}
            onClick={handleJoinConsultation}
            disabled={busy || loggingOut}
            title={loggingOut ? "Odjava je u toku." : undefined}
          >
            Pridruži se pozivu
          </button>
          <button
            className={styles.dismissButton}
            onClick={handleDismiss}
            aria-label="Odbaci obaveštenje"
            disabled={busy}
            title={busy ? "Odbijam..." : "Odbaci obaveštenje"}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
};

export default VideoConsultAlert;
